/*
 * Declarative shadow DOM is only initialized on the initial render of the page.
 * If the component is mounted after the browser finishes the initial render,
 * the shadow root needs to be manually hydrated.
 */
export class DeclarativeShadowElement extends HTMLElement {
  connectedCallback() {
    if (!this.shadowRoot) {
      const template = this.querySelector(':scope > template[shadowrootmode="open"]');

      if (!(template instanceof HTMLTemplateElement)) return;

      const shadow = this.attachShadow({ mode: 'open' });
      shadow.append(template.content.cloneNode(true));
    }
  }
}

/**
 * A custom ResizeObserver that only calls the callback when the element is resized.
 * By default the ResizeObserver callback is called when the element is first observed.
 */
export class ResizeNotifier extends ResizeObserver {
  #initialized = false;

  /**
   * @param {ResizeObserverCallback} callback
   */
  constructor(callback) {
    super((entries) => {
      if (this.#initialized) return callback(entries, this);
      this.#initialized = true;
    });
  }

  disconnect() {
    this.#initialized = false;
    super.disconnect();
  }
}

/**
 * Event class for overflow minimum items updates
 * @extends {Event}
 */
export class OverflowMinimumEvent extends Event {
  /**
   * Creates a new OverflowMinimumEvent
   * @param {boolean} minimumReached - Whether the minimum number of visible items has been reached
   */
  constructor(minimumReached) {
    super('overflowMinimum', { bubbles: true });
    this.detail = {
      minimumReached,
    };
  }
}

export class ReflowEvent extends Event {
  /**
   * @param {HTMLElement} lastVisibleElement - The element to move to the last visible position
   */
  constructor(lastVisibleElement) {
    super('reflow', { bubbles: true });
    this.detail = { lastVisibleElement };
  }
}

/**
 * A custom element that wraps a list of items and moves them to an overflow slot when they don't fit.
 * This component is used in the header section, it needs to be render-blocking to avoid layout shifts.
 * @attr {string | null} minimum-items When set, the element enters a 'minimum-reached' state when visible items are at or below this number.
 * @example
 * <overflow-list minimum-items="2">
 *   <!-- list items -->
 * </overflow-list>
 */
export class OverflowList extends DeclarativeShadowElement {
  static get observedAttributes() {
    return ['disabled', 'minimum-items'];
  }

  /**
   * @param {string} name
   * @param {string} oldValue
   * @param {string} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'disabled') {
      if (newValue === 'true') {
        this.#reset();
      } else {
        this.#reflowItems();
      }
    }
  }

  connectedCallback() {
    super.connectedCallback();

    if (this.hasAttribute('defer')) {
      const deferredReflow = () => {
        // Remove attribute first to change layout before calculating the actual size
        this.removeAttribute('defer');
        this.#initialize();
      };
      const { schedule } = this;

      const requestIdleCallback =
        typeof window.requestIdleCallback === 'function' ? window.requestIdleCallback : setTimeout;

      requestIdleCallback(() => schedule(deferredReflow));
    } else if (this.shadowRoot) {
      this.#initialize();
    } else {
      // Not all element children has been parsed yet, try again in the next tick
      // <overflow-list> is a special case as critical.js can execute before DOMContentLoaded
      setTimeout(() => {
        this.#initialize();
      }, 0);
    }
  }

  /**
   * Initialize the element
   */
  #initialize() {
    const { shadowRoot } = this;

    if (!shadowRoot) throw new Error('Missing shadow root');

    const defaultSlot = shadowRoot.querySelector('slot:not([name])');
    const overflowSlot = shadowRoot.querySelector('slot[name="overflow"]');
    const moreSlot = shadowRoot.querySelector('slot[name="more"]');
    const overflow = shadowRoot.querySelector('[part="overflow"]');
    const list = shadowRoot.querySelector('[part="list"]');
    const placeholder = shadowRoot.querySelector('[part="placeholder"]');

    if (
      !(defaultSlot instanceof HTMLSlotElement) ||
      !(overflowSlot instanceof HTMLSlotElement) ||
      !(moreSlot instanceof HTMLSlotElement) ||
      !(overflow instanceof HTMLElement) ||
      !(list instanceof HTMLUListElement) ||
      !(placeholder instanceof HTMLLIElement)
    ) {
      throw new Error('Invalid element types in <OverflowList />');
    }

    this.#refs = {
      defaultSlot,
      overflowSlot,
      moreSlot,
      overflow,
      list,
      placeholder,
    };

    // Add event listener for reflow requests
    this.addEventListener(
      'reflow',
      /** @param {ReflowEvent} event */ (event) => {
        this.#reflowItems(event.detail.lastVisibleElement);
      }
    );

    this.#reflowItems();
  }

  disconnectedCallback() {
    this.#resizeObserver.disconnect();
  }

  get schedule() {
    return typeof Theme?.utilities?.scheduler?.schedule === 'function'
      ? Theme.utilities.scheduler.schedule
      : /** @param {FrameRequestCallback} callback */ (callback) =>
          requestAnimationFrame(() => setTimeout(callback, 0));
  }

  #scheduled = false;

  /**
   * Get the minimum number of items before changing the minimum-reached state
   * @returns {number | null}
   */
  get minimumItems() {
    const value = this.getAttribute('minimum-items');
    return value ? parseInt(value, 10) : null;
  }

  get overflowSlot() {
    const { overflowSlot } = this.#refs;
    return overflowSlot;
  }

  get defaultSlot() {
    const { defaultSlot } = this.#refs;
    return defaultSlot;
  }

  /**
   * @type {{width: number | null, height: number | null}}
   */
  #lastDimensions = {
    width: null,
    height: null,
  };

  /**
   * @type {ResizeObserverCallback & MutationCallback}
   */
  #handleChange = (event) => {
    if (this.#scheduled) return;

    let width = null;
    let height = null;
    let isResize = false;

    for (const [, entry] of event.entries()) {
      if (!(entry instanceof ResizeObserverEntry)) break;
      // There should only be one entry
      isResize = true;
      width = Math.round(entry.contentRect.width);
      height = Math.round(entry.contentRect.height);
    }

    if (isResize) {
      if (!width || !height || (width === this.#lastDimensions.width && height === this.#lastDimensions.height)) {
        // Skip reflow if dimensions are 0 or the same as the last reflow
        return;
      }

      this.#lastDimensions = { width: Math.round(width), height: Math.round(height) };
    }

    this.#scheduled = true;

    this.schedule(() => {
      this.#reflowItems();
      this.#scheduled = false;
    });
  };

  /**
   * Move all items to the default slot.
   */
  #moveItemsToDefaultSlot() {
    const { defaultSlot, overflowSlot } = this.#refs;

    for (const element of overflowSlot.assignedElements()) {
      element.slot = defaultSlot.name;
    }
  }

  /**
   * Reset the list to its initial state and disconnect the observers.
   */
  #reset() {
    const { list } = this.#refs;

    this.#mutationObserver.disconnect();
    this.#resizeObserver.disconnect();
    this.#moveItemsToDefaultSlot();

    list.style.removeProperty('height');
    this.style.setProperty('--overflow-count', '0');
  }

  /**
   * Sets the minimum-reached attribute and dispatches a custom event based on visible elements count
   * @param {Element[]} visibleElements - The currently visible elements
   */
  #updateMinimumReached(visibleElements) {
    if (this.minimumItems !== null) {
      const minimumReached = visibleElements.length < this.minimumItems;

      if (minimumReached) {
        this.setAttribute('minimum-reached', '');
      } else {
        this.removeAttribute('minimum-reached');
      }

      this.dispatchEvent(new OverflowMinimumEvent(minimumReached));
    }
  }

  /**
   * Show all items in the list.
   */
  showAll() {
    const { placeholder } = this.#refs;

    placeholder.style.setProperty('width', '0');
    placeholder.style.setProperty('display', 'none');
    this.setAttribute('disabled', 'true');
  }

  /**
   * Reflow items based on available space within the list.
   * @param {HTMLElement} [lastVisibleElement] Optional element to place in last visible position
   */
  #reflowItems = (lastVisibleElement) => {
    const { defaultSlot, overflowSlot, moreSlot, list, placeholder } = this.#refs;

    this.#resizeObserver.disconnect();
    this.#mutationObserver.disconnect();

    // Avoid layout shifts while reflowing the list
    const { height } = this.firstElementChild?.getBoundingClientRect() ?? {};

    if (height) list.style.height = `${height}px`;
    list.style.setProperty('overflow', 'hidden');

    // Move all elements to the default slot so we can check which ones overflow
    this.#moveItemsToDefaultSlot();

    const elements = defaultSlot.assignedElements();

    // Make sure the "More" item and placeholder are hidden
    moreSlot.hidden = true;
    placeholder.hidden = true;

    // First, check if all the items fit
    const rootRect = list.getBoundingClientRect();

    // Store the initial dimensions for comparison later
    this.#lastDimensions = { width: Math.round(rootRect.width), height: Math.round(rootRect.height) };

    const getVisibleElements = () => elements.filter((el) => el.getBoundingClientRect().top <= rootRect.top);
    let visibleElements = getVisibleElements();

    // If not all items fit or we have a lastVisibleElement, let's calculate with "More" button
    if (visibleElements.length !== elements.length || lastVisibleElement) {
      // Putting the "More" item (and lastVisibleElement, if provided) at the start of the list lets us see which items will fit on the same row
      moreSlot.style.setProperty('order', '-1');
      moreSlot.hidden = false;
      moreSlot.style.setProperty('height', `${height}px`);

      if (lastVisibleElement) {
        lastVisibleElement.style.setProperty('order', '-1');
      }

      // Recalculate the visible elements
      visibleElements = getVisibleElements();

      // Reset the order
      moreSlot.style.removeProperty('order');
      if (lastVisibleElement) {
        lastVisibleElement.style.removeProperty('order');
      }

      // If we have a lastVisibleElement, ensure it's in the last visible position
      if (lastVisibleElement) {
        const visibleIndex = visibleElements.indexOf(lastVisibleElement);
        if (visibleIndex !== -1) {
          // Remove lastVisibleElement from its current position
          visibleElements.splice(visibleIndex, 1);
          // Add it to the end of visible elements
          visibleElements.push(lastVisibleElement);
        }
      }

      moreSlot.style.setProperty('height', 'auto');
    }

    const overflowingElements = elements.filter((element) => !visibleElements.includes(element));
    const [firstOverflowingElement] = overflowingElements;
    const hasOverflow = overflowingElements.length > 0;
    const placeholderWidth = firstOverflowingElement ? firstOverflowingElement.clientWidth : 0;

    // Move the elements to the correct slot
    for (const element of elements) {
      element.slot = overflowingElements.includes(element) ? overflowSlot.name : defaultSlot.name;
    }

    // If there are overflowing elements
    // Show more button and placeholder if needed
    moreSlot.hidden = !hasOverflow;

    if (hasOverflow) {
      // Set the width and height of the placeholder so the list can grow if there is space
      placeholder.style.width = `${placeholderWidth}px`;
      placeholder.hidden = false;
    }

    list.style.setProperty('counter-reset', `overflow-count ${overflowingElements.length}`);
    this.style.setProperty('--overflow-count', `${overflowingElements.length}`);
    list.style.removeProperty('overflow');

    // Check if the minimum-reached state should be updated
    hasOverflow && this.#updateMinimumReached(visibleElements);

    // Observe the list for changes in size
    this.#resizeObserver.observe(this);
    this.#mutationObserver.observe(this, { childList: true });
  };

  /**
   * @type {{
   *   defaultSlot: HTMLSlotElement;
   *   overflowSlot: HTMLSlotElement;
   *   moreSlot: HTMLSlotElement;
   *   overflow: HTMLElement;
   *   list: HTMLUListElement;
   *   placeholder: HTMLLIElement;
   * }}
   */
  #refs;

  /**
   * @type {ResizeObserver}
   */
  #resizeObserver = new ResizeNotifier(this.#handleChange);

  /**
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver(this.#handleChange);
}

if (!customElements.get('overflow-list')) {
  customElements.define('overflow-list', OverflowList);
}

// Function to calculate total height of header group children
export function calculateHeaderGroupHeight(
  header = document.querySelector('#header-component'),
  headerGroup = document.querySelector('#header-group')
) {
  if (!headerGroup) return 0;

  let totalHeight = 0;
  const children = headerGroup.children;
  for (let i = 0; i < children.length; i++) {
    const element = children[i];
    if (element === header || !(element instanceof HTMLElement)) continue;
    totalHeight += element.offsetHeight;
  }

  // If the header is transparent and has a sibling section, add the height of the header to the total height
  if (header instanceof HTMLElement && header.hasAttribute('transparent') && header.parentElement?.nextElementSibling) {
    return totalHeight + header.offsetHeight;
  }

  return totalHeight;
}

/**
 * Initialize and maintain header height CSS variables.
 * This is critical for preventing layout shifts during page load.
 * There is a `ResizeObserver` and `MutationObserver` that kicks in post hydration in header.js
 * Note: header-group uses display: contents, so we must observe all children.
 */
function updateHeaderHeights() {
  const header = document.querySelector('header-component');

  // Early exit if no header - nothing to do
  if (!(header instanceof HTMLElement)) return;

  // Calculate initial height(s
  const headerHeight = header.offsetHeight;
  const headerGroupHeight = calculateHeaderGroupHeight(header);

  document.body.style.setProperty('--header-height', `${headerHeight}px`);
  document.body.style.setProperty('--header-group-height', `${headerGroupHeight}px`);
}

/**
 * Updates CSS custom properties for transparent header offset calculation
 * Avoids expensive :has() selectors
 */
function updateTransparentHeaderOffset() {
  const header = document.querySelector('#header-component');
  const headerGroup = document.querySelector('#header-group');
  const hasHeaderSection = headerGroup?.querySelector('.header-section');
  if (!hasHeaderSection || !header?.hasAttribute('transparent')) {
    document.body.style.setProperty('--transparent-header-offset-boolean', '0');
    return;
  }

  const hasImmediateSection = hasHeaderSection.nextElementSibling?.classList.contains('shopify-section');

  const shouldApplyOffset = !hasImmediateSection ? '1' : '0';
  document.body.style.setProperty('--transparent-header-offset-boolean', shouldApplyOffset);
}

export function updateAllHeaderCustomProperties() {
  updateHeaderHeights();
  updateTransparentHeaderOffset();
}

// Run both functions on page load
updateAllHeaderCustomProperties();

function applyMiyabiyaStorefrontFixes() {
  const isJapanese = window.location.pathname === '/ja' || window.location.pathname.startsWith('/ja/');
  const isJapaneseHome =
    isJapanese &&
    (window.location.pathname === '/ja' ||
      window.location.pathname === '/ja/' ||
      window.location.pathname === '/ja-jp' ||
      window.location.pathname === '/ja-jp/');

  document.querySelectorAll('.miyabiya-search-input').forEach((input) => {
    if (!(input instanceof HTMLInputElement)) return;
    input.placeholder = isJapanese ? '作品名・商品名で検索' : 'Search anime series or products';
  });

  document.querySelectorAll('.footer-utilities__group-copyright > .footer-utilities__text').forEach((node) => {
    if (!(node instanceof HTMLElement)) return;
    if (node.textContent?.includes('All rights reserved')) return;
    const rootPath = isJapanese ? '/ja' : '/';
    node.innerHTML = `&copy; 2026 <a href="${rootPath}">MIYABIYA</a>. All rights reserved.`;
  });

  if (!document.getElementById('miyabiya-franchise-nav-pc-fit')) {
    const style = document.createElement('style');
    style.id = 'miyabiya-franchise-nav-pc-fit';
    style.textContent = `
      @media screen and (min-width: 750px) {
        body .franchise-nav__inner {
          width: 100% !important;
          max-width: var(--page-width) !important;
          padding-inline: 24px !important;
          overflow-x: visible !important;
        }

        body .franchise-nav__group,
        body .franchise-nav__chips {
          flex: 1 1 100% !important;
          width: 100% !important;
          min-width: 0 !important;
        }

        body .franchise-nav__chips {
          display: grid !important;
          grid-template-columns: repeat(9, minmax(0, 1fr)) !important;
          gap: 8px !important;
        }

        body .franchise-nav__chip {
          width: 100% !important;
          min-width: 0 !important;
          padding-inline: 8px !important;
          border-radius: 4px !important;
        }
      }

      body .franchise-nav__chip {
        border-radius: 4px !important;
      }
    `;
    document.head.appendChild(style);
  }

  document.querySelectorAll('.franchise-nav').forEach((nav) => {
    nav.querySelectorAll('.franchise-nav__label, .franchise-nav__divider').forEach((element) => element.remove());

    const firstGroup = nav.querySelector('.franchise-nav__group');
    const chips = firstGroup?.querySelector('.franchise-nav__chips');
    if (!(chips instanceof HTMLElement)) return;

    const rootPath = isJapanese ? '/ja' : '';
    const allProductsPath = `${rootPath}/collections/all`;
    const quickLinks = [
      [isJapanese ? '新着商品' : 'New Arrivals', allProductsPath],
      [isJapanese ? '人気商品' : 'Popular Items', allProductsPath],
      [isJapanese ? '作品から探す' : 'Browse by Series', `${rootPath}/pages/series`],
      [isJapanese ? 'キャラクター検索' : 'Character Search', `${rootPath}/pages/character-search`],
      [isJapanese ? 'フィギュア' : 'Figures', `${rootPath}/collections/figures`],
      [isJapanese ? 'ぬいぐるみ' : 'Plush', `${rootPath}/search?q=${encodeURIComponent(isJapanese ? 'ぬいぐるみ' : 'plush')}&type=product`],
      [isJapanese ? 'アクスタ' : 'Acrylic Stands', `${rootPath}/search?q=${encodeURIComponent(isJapanese ? 'アクスタ' : 'acrylic stand')}&type=product`],
      [isJapanese ? 'カード' : 'Cards', `${rootPath}/collections/cards`],
      ['SALE', `${rootPath}/search?q=SALE&type=product&options%5Bprefix%5D=last`],
    ];

    chips.textContent = '';

    const fragment = document.createDocumentFragment();
    quickLinks.forEach(([label, href]) => {
      const link = document.createElement('a');
      link.className = 'franchise-nav__chip';
      link.href = href;
      link.setAttribute('role', 'listitem');
      link.textContent = label;
      fragment.append(link);
    });
    chips.prepend(fragment);

    nav.querySelectorAll('.franchise-nav__group').forEach((group) => {
      if (group !== firstGroup) group.remove();
    });
  });

  document.querySelectorAll('.miyabiya-mobile-drawer a').forEach((link) => {
    const label = (link.textContent || '').replace(/\s+/g, ' ').trim().toLowerCase();
    if (label.includes('セール') || label.includes('sale')) {
      link.href = `${isJapanese ? '/ja' : ''}/search?q=SALE&type=product&options%5Bprefix%5D=last`;
    }
    if (label.includes('予約受付中') || label.includes('pre-order')) {
      link.href = isJapanese
        ? '/ja/search?q=%E4%BA%88%E7%B4%84%E5%8F%97%E4%BB%98%E4%B8%AD&type=product&options%5Bprefix%5D=last'
        : '/search?q=pre-order&type=product&options%5Bprefix%5D=last';
    }
  });

  if (isJapanese) {
    document.querySelectorAll('.menu-drawer__menu-item-text').forEach((label) => {
      if (!(label instanceof HTMLElement)) return;
      const text = (label.textContent || '').replace(/\s+/g, ' ').trim();
      if (text === 'Home') label.textContent = 'ホーム';
      if (text === 'Catalog') label.textContent = '商品一覧';
      if (text === 'Inquiries' || text === 'Contact' || text === 'Contact us') {
        label.textContent = 'お問い合わせ';
      }
    });

    document.querySelectorAll('.miyabiya-jp-drawer-menu').forEach((menu) => {
      const defaultMenu = menu.nextElementSibling;
      if (defaultMenu instanceof HTMLElement && defaultMenu.classList.contains('menu-drawer__menu')) {
        defaultMenu.style.setProperty('display', 'none', 'important');
      }
    });
  }

  if (isJapaneseHome) {
    document.querySelectorAll('.section-resource-list__header').forEach((header) => {
      const heading = header.querySelector('h1, h2, h3, h4, h5, h6');
      if (heading instanceof HTMLElement) {
        const headingText = (heading.textContent || '').replace(/\s+/g, ' ').trim();
        if (
          headingText === 'Currently Trending' ||
          headingText === '現在人気の商品' ||
          headingText === 'Popular Items'
        ) {
          heading.textContent = '新着商品';
        }
      }

      header.querySelectorAll('a').forEach((link) => {
        const linkText = (link.textContent || '').replace(/\s+/g, ' ').trim();
        if (linkText === 'View all' || linkText === '人気商品一覧' || linkText === '新着一覧') {
          link.textContent = '新着一覧';
          link.setAttribute('href', '/ja/collections/all');
        }
      });
    });
  }

  if (!document.body.classList.contains('template-product')) return;

  const mediaContainer = document.querySelector('.product-information__media');
  if (!(mediaContainer instanceof HTMLElement)) return;
  if (mediaContainer.querySelector('.product-media__image, .miyabiya-mobile-product-image__img')) return;

  const sectionRoot = mediaContainer.closest('.shopify-section');
  if (!(sectionRoot instanceof HTMLElement) || !sectionRoot.id.startsWith('shopify-section-')) return;

  const sectionId = sectionRoot.id.replace('shopify-section-', '');
  const refreshUrl = new URL(window.location.href);
  refreshUrl.searchParams.set('sections', sectionId);
  refreshUrl.searchParams.set('_miyabiya_media_refresh', Date.now().toString());

  fetch(refreshUrl.toString(), { credentials: 'same-origin' })
    .then((response) => response.ok ? response.json() : null)
    .then((sections) => {
      const sectionHtml = sections?.[sectionId];
      if (!sectionHtml) return;

      const parsed = new DOMParser().parseFromString(sectionHtml, 'text/html');
      const freshMedia = parsed.querySelector('.product-information__media');
      if (!(freshMedia instanceof HTMLElement)) return;
      if (!freshMedia.querySelector('.product-media__image, .miyabiya-mobile-product-image__img')) return;

      mediaContainer.replaceWith(freshMedia);
    })
    .catch(() => {});
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', applyMiyabiyaStorefrontFixes, { once: true });
} else {
  applyMiyabiyaStorefrontFixes();
}

function applyMiyabiyaEnglishHomeHeroFallback() {
  const path = window.location.pathname;
  const isEnglishHome = path === '/' || path === '';
  if (!isEnglishHome) return;

  document.querySelectorAll('.miyabiya-en-mobile-announcement, .miyabiya-market-proof').forEach((element) => {
    if (element instanceof HTMLElement) element.style.setProperty('display', 'none', 'important');
  });

  document.querySelectorAll('[aria-label="Curated collections"], .miyabiya-category-hub').forEach((element) => {
    if (element instanceof HTMLElement) element.remove();
  });

  const assetBase = '/cdn/shop/t/21/assets/';
  const cacheBust = `?v=${Date.now()}`;
  const heroDesktop = document.querySelector('.miyabiya-home-hero .miyabiya-jp-hero-img--desktop');
  const heroMobile = document.querySelector('.miyabiya-home-hero .miyabiya-jp-hero-img--mobile');
  if (heroDesktop instanceof HTMLImageElement) {
    heroDesktop.src = `${assetBase}miyabiya-en-hero-desktop-chibi-staff-wide-v3.png${cacheBust}`;
    heroDesktop.alt = 'MIYABIYA specialty store for figures and hobby goods';
  }
  if (heroMobile instanceof HTMLImageElement) {
    heroMobile.src = `${assetBase}miyabiya-en-hero-mobile-chibi-staff-v3.png${cacheBust}`;
    heroMobile.alt = 'MIYABIYA specialty store for figures and hobby goods mobile banner';
  }

  const bannerData = [
    ['.miyabiya-home-feature-banners__item--new-arrival', '/collections/all', 'miyabiya-banner-new-arrival-en-v3.png', 'miyabiya-banner-new-arrival-en-mobile-fit-v3.png', 'Fresh arrivals. Latest figures and collectibles from Japan.', 'View new arrivals'],
    ['.miyabiya-home-feature-banners__item--preorder', '/collections/pre-order', 'miyabiya-banner-preorder-en-v3.png', 'miyabiya-banner-preorder-en-mobile-fit-v3.png', 'Pre-orders open. Reserve upcoming figures before release.', 'View pre-orders'],
    ['.miyabiya-home-feature-banners__item--restock', '/collections/restock', 'miyabiya-banner-restock-en-v3.png', 'miyabiya-banner-restock-en-mobile-fit-v3.png', 'Back in stock. Popular items are available again.', 'View restocked items'],
    ['.miyabiya-home-feature-banners__item--last-one', '/collections/last-one', 'miyabiya-banner-last-one-en-v3.png', 'miyabiya-banner-last-one-en-mobile-fit-v3.png', "Almost gone. Limited stock remaining. Don't miss out.", 'View last one items'],
  ];

  bannerData.forEach(([selector, href, desktopImage, mobileImage, alt, label]) => {
    const link = document.querySelector(selector);
    if (!(link instanceof HTMLAnchorElement)) return;
    link.href = href;
    link.setAttribute('aria-label', label);
    const source = link.querySelector('source');
    if (source instanceof HTMLSourceElement) source.srcset = `${assetBase}${mobileImage}${cacheBust}`;
    const img = link.querySelector('img');
    if (img instanceof HTMLImageElement) {
      img.src = `${assetBase}${desktopImage}${cacheBust}`;
      img.alt = alt;
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', applyMiyabiyaEnglishHomeHeroFallback, { once: true });
} else {
  applyMiyabiyaEnglishHomeHeroFallback();
}

function normalizeMiyabiyaProductBreadcrumbs() {
  if (!window.location.pathname.includes('/products/')) return;

  const styleId = 'miyabiya-product-breadcrumb-fallback-style';
  if (!document.getElementById(styleId)) {
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      @media (max-width: 749px) {
        main[data-template="product"] .franchise-nav,
        body:has(main[data-template="product"]) .franchise-nav {
          display: none !important;
        }

        main[data-template="product"] .product-information__grid {
          display: flex !important;
          flex-direction: column !important;
        }

        main[data-template="product"] .product-information__grid > .miyabiya-product-breadcrumb {
          order: 1 !important;
          grid-column: auto !important;
          margin: 10px 16px 8px !important;
        }

        main[data-template="product"] .product-information__media {
          order: 3 !important;
          width: 100% !important;
          height: auto !important;
          min-height: 0 !important;
          margin: 20px 0 16px !important;
          padding: 0 !important;
        }

        main[data-template="product"] .miyabiya-product-breadcrumb {
          box-sizing: border-box !important;
          order: 1 !important;
          width: calc(100% - 32px) !important;
          max-width: calc(100% - 32px) !important;
          margin: 10px 16px 8px !important;
          padding: 0 0 2px !important;
          color: #6b7280 !important;
          font-size: 12px !important;
          line-height: 1.4 !important;
        }

        main[data-template="product"] .miyabiya-mobile-product-title {
          display: block !important;
          order: 2 !important;
          margin: 4px 16px 10px !important;
          color: #171717 !important;
          font-size: clamp(1rem, 4.15vw, 1.1rem) !important;
          font-weight: 500 !important;
          line-height: 1.25 !important;
          letter-spacing: 0 !important;
        }

        main[data-template="product"] .product-details .text-block:is(.h1, .h2) {
          display: none !important;
          width: 0 !important;
          height: 0 !important;
          min-height: 0 !important;
          margin: 0 !important;
          padding: 0 !important;
          overflow: hidden !important;
        }

        main[data-template="product"] .miyabiya-mobile-product-image {
          box-sizing: border-box !important;
          width: clamp(288px, calc(100vw - 32px), 398px) !important;
          max-width: clamp(288px, calc(100vw - 32px), 398px) !important;
          height: auto !important;
          min-height: 0 !important;
          max-height: 320px !important;
          margin: 0 auto 12px !important;
          padding: 14px 12px !important;
          background: #f7f7f7 !important;
          align-items: center !important;
          justify-content: center !important;
          overflow: hidden !important;
        }

        main[data-template="product"] .miyabiya-mobile-product-image__img {
          display: block !important;
          width: auto !important;
          max-width: 100% !important;
          height: auto !important;
          max-height: 100% !important;
          margin: 0 auto !important;
          object-fit: contain !important;
        }

        main[data-template="product"] .miyabiya-mobile-product-gallery {
          box-sizing: border-box !important;
          width: 100% !important;
          height: auto !important;
          min-height: 0 !important;
          margin: 0 auto 12px !important;
          padding: 0 max(16px, calc((100vw - clamp(288px, calc(100vw - 32px), 398px)) / 2)) 0 !important;
          gap: 12px !important;
          overflow-x: auto !important;
          scroll-snap-type: x mandatory !important;
        }

        main[data-template="product"] .miyabiya-mobile-product-gallery__item {
          box-sizing: border-box !important;
          flex: 0 0 clamp(288px, calc(100vw - 32px), 398px) !important;
          width: clamp(288px, calc(100vw - 32px), 398px) !important;
          max-width: clamp(288px, calc(100vw - 32px), 398px) !important;
          height: clamp(288px, calc(100vw - 32px), 398px) !important;
          max-height: 320px !important;
          margin: 0 !important;
          padding: 12px !important;
          align-items: center !important;
          justify-content: center !important;
          overflow: hidden !important;
          scroll-snap-align: center !important;
        }

        main[data-template="product"] .miyabiya-mobile-product-gallery__img {
          display: block !important;
          width: auto !important;
          max-width: 100% !important;
          height: auto !important;
          max-height: 100% !important;
          margin: 0 auto !important;
          object-fit: contain !important;
        }

        main[data-template="product"] .product-details {
          order: 4 !important;
          width: 100% !important;
          max-width: 100% !important;
          margin-top: 0 !important;
          padding-top: 0 !important;
        }

        main[data-template="product"] .product-details > .group-block {
          width: 100% !important;
          max-width: 100% !important;
          margin-top: 0 !important;
          padding-top: 0 !important;
          padding-block-start: 0 !important;
          padding-block-end: 12px !important;
        }

        main[data-template="product"] .product-details > .group-block > .group-block-content {
          margin-top: 0 !important;
          padding-top: 0 !important;
          gap: 8px !important;
        }

        main[data-template="product"] .product-information__media > media-gallery,
        main[data-template="product"] .product-information__media .thumbnail-list,
        main[data-template="product"] .product-information__media .dialog-thumbnails-list {
          display: none !important;
          height: 0 !important;
          min-height: 0 !important;
          margin: 0 !important;
          padding: 0 !important;
          overflow: hidden !important;
        }

	        html body:has(main[data-template="product"]) main[data-template="product"] .product-details product-price,
	        html body:has(main[data-template="product"]) main[data-template="product"] .product-details .variant-picker,
	        html body:has(main[data-template="product"]) main[data-template="product"] .product-details .buy-buttons-block,
	        html body:has(main[data-template="product"]) main[data-template="product"] .product-details .product-form,
	        html body:has(main[data-template="product"]) main[data-template="product"] .product-details .accelerated-checkout-block {
	          box-sizing: border-box !important;
	          width: clamp(288px, calc(100vw - 32px), 398px) !important;
	          max-width: clamp(288px, calc(100vw - 32px), 398px) !important;
	          margin-left: auto !important;
	          margin-right: auto !important;
	        }

	        html body:has(main[data-template="product"]) main[data-template="product"] .product-details product-form-component,
	        html body:has(main[data-template="product"]) main[data-template="product"] .product-details .shopify-product-form,
	        html body:has(main[data-template="product"]) main[data-template="product"] .product-details .product-form-buttons,
	        html body:has(main[data-template="product"]) main[data-template="product"] .product-details .miyabiya-wishlist-product-button,
	        html body:has(main[data-template="product"]) main[data-template="product"] .product-details .miyabiya-product-proof {
	          box-sizing: border-box !important;
	          width: 100% !important;
	          max-width: 100% !important;
	        }

	        html body:has(main[data-template="product"]) main[data-template="product"] .product-details .product-form-buttons {
	          flex-wrap: wrap !important;
	          justify-content: center !important;
	          gap: 8px !important;
	        }

	        html body:has(main[data-template="product"]) main[data-template="product"] .product-details .product-form-buttons > *:not(.quantity-selector, .quantity-rules, .quantity-label, .accelerated-checkout-block) {
	          flex: 1 1 auto !important;
	          min-width: 0 !important;
	        }

	        html body:has(main[data-template="product"]) main[data-template="product"] .product-details .product-form-buttons .accelerated-checkout-block {
	          flex: 0 0 100% !important;
	          width: 100% !important;
	          margin-top: 8px !important;
	        }

	        html body:has(main[data-template="product"]) main[data-template="product"] .product-details .quantity-selector {
	          flex: 0 0 clamp(126px, 32vw, 140px) !important;
	        }
	      }
	    `;
    document.head.appendChild(style);
  }
  const fallbackStyle = document.getElementById(styleId);
  if (fallbackStyle && !fallbackStyle.textContent.includes('main[data-template="product"] .product-details')) {
    fallbackStyle.textContent += `
      @media (max-width: 749px) {
        main[data-template="product"] .product-details {
          order: 4 !important;
        }
      }
    `;
  }
  if (fallbackStyle && !fallbackStyle.textContent.includes('miyabiya-wishlist-product-button')) {
    fallbackStyle.textContent += `
      @media (max-width: 749px) {
        main[data-template="product"] .product-details product-price,
        main[data-template="product"] .product-details .variant-picker,
        main[data-template="product"] .product-details .buy-buttons-block,
        main[data-template="product"] .product-details .product-form,
        main[data-template="product"] .product-details .accelerated-checkout-block {
          box-sizing: border-box !important;
          width: clamp(288px, calc(100vw - 32px), 398px) !important;
          max-width: clamp(288px, calc(100vw - 32px), 398px) !important;
          margin-left: auto !important;
          margin-right: auto !important;
        }

        main[data-template="product"] .product-details product-form-component,
        main[data-template="product"] .product-details .shopify-product-form,
        main[data-template="product"] .product-details .product-form-buttons,
        main[data-template="product"] .product-details .miyabiya-wishlist-product-button,
        main[data-template="product"] .product-details .miyabiya-product-proof {
          box-sizing: border-box !important;
          width: 100% !important;
          max-width: 100% !important;
        }

        main[data-template="product"] .product-details .product-form-buttons {
          flex-wrap: wrap !important;
          justify-content: center !important;
          gap: 8px !important;
        }

        main[data-template="product"] .product-details .product-form-buttons > *:not(.quantity-selector, .quantity-rules, .quantity-label, .accelerated-checkout-block) {
          flex: 1 1 auto !important;
          min-width: 0 !important;
        }

        main[data-template="product"] .product-details .product-form-buttons .accelerated-checkout-block {
          flex: 0 0 100% !important;
          width: 100% !important;
          margin-top: 8px !important;
        }

        main[data-template="product"] .product-details .quantity-selector {
          flex: 0 0 clamp(126px, 32vw, 140px) !important;
        }
      }
    `;
  }
  if (fallbackStyle && !fallbackStyle.textContent.includes('miyabiya-mobile-product-image__img')) {
    fallbackStyle.textContent += `
      @media (max-width: 749px) {
        main[data-template="product"] .product-information__media {
          order: 3 !important;
          width: 100% !important;
          height: auto !important;
          min-height: 0 !important;
          margin: 20px 0 16px !important;
          padding: 0 !important;
        }

        main[data-template="product"] .miyabiya-mobile-product-title {
          display: block !important;
          order: 2 !important;
          margin: 4px 16px 10px !important;
          color: #171717 !important;
          font-size: clamp(1rem, 4.15vw, 1.1rem) !important;
          font-weight: 500 !important;
          line-height: 1.25 !important;
          letter-spacing: 0 !important;
        }

        main[data-template="product"] .product-details .text-block:is(.h1, .h2) {
          display: none !important;
          width: 0 !important;
          height: 0 !important;
          min-height: 0 !important;
          margin: 0 !important;
          padding: 0 !important;
          overflow: hidden !important;
        }

        main[data-template="product"] .miyabiya-mobile-product-image {
          box-sizing: border-box !important;
          width: clamp(288px, calc(100vw - 32px), 398px) !important;
          max-width: clamp(288px, calc(100vw - 32px), 398px) !important;
          height: auto !important;
          min-height: 0 !important;
          max-height: 320px !important;
          margin: 0 auto 12px !important;
          padding: 14px 12px !important;
          background: #f7f7f7 !important;
          align-items: center !important;
          justify-content: center !important;
          overflow: hidden !important;
        }

        main[data-template="product"] .miyabiya-mobile-product-image__img {
          display: block !important;
          width: auto !important;
          max-width: 100% !important;
          height: auto !important;
          max-height: 100% !important;
          margin: 0 auto !important;
          object-fit: contain !important;
        }

        main[data-template="product"] .miyabiya-mobile-product-gallery {
          box-sizing: border-box !important;
          width: 100% !important;
          height: auto !important;
          min-height: 0 !important;
          margin: 0 auto 12px !important;
          padding: 0 max(16px, calc((100vw - clamp(288px, calc(100vw - 32px), 398px)) / 2)) 0 !important;
          gap: 12px !important;
          overflow-x: auto !important;
          scroll-snap-type: x mandatory !important;
        }

        main[data-template="product"] .miyabiya-mobile-product-gallery__item {
          box-sizing: border-box !important;
          flex: 0 0 clamp(288px, calc(100vw - 32px), 398px) !important;
          width: clamp(288px, calc(100vw - 32px), 398px) !important;
          max-width: clamp(288px, calc(100vw - 32px), 398px) !important;
          height: clamp(288px, calc(100vw - 32px), 398px) !important;
          max-height: 320px !important;
          margin: 0 !important;
          padding: 12px !important;
          align-items: center !important;
          justify-content: center !important;
          overflow: hidden !important;
          scroll-snap-align: center !important;
        }

        main[data-template="product"] .miyabiya-mobile-product-gallery__img {
          display: block !important;
          width: auto !important;
          max-width: 100% !important;
          height: auto !important;
          max-height: 100% !important;
          margin: 0 auto !important;
          object-fit: contain !important;
        }
      }
    `;
  }

  const inlineNav = document.querySelector('.miyabiya-mobile-product-breadcrumb-inline');
  const useInlineBreadcrumb = window.matchMedia('(max-width: 749px)').matches && inlineNav instanceof HTMLElement;
  if (useInlineBreadcrumb) {
    document.querySelectorAll('.miyabiya-product-breadcrumb').forEach((breadcrumb) => breadcrumb.remove());
  }

  let nav = useInlineBreadcrumb ? inlineNav : document.querySelector('.miyabiya-product-breadcrumb');
  if (!nav) {
    const grid = document.querySelector('main[data-template="product"] .product-information__grid, .product-information__grid');
    const media = grid?.querySelector('.product-information__media');
    nav = document.createElement('nav');
    nav.className = 'miyabiya-product-breadcrumb miyabiya-product-breadcrumb--fallback';
    if (media) {
      grid.insertBefore(nav, media);
    } else if (grid) {
      grid.prepend(nav);
    } else {
      document.querySelector('main')?.prepend(nav);
    }
  }

  const isJapanese = document.documentElement.lang.toLowerCase().startsWith('ja') || window.location.pathname.startsWith('/ja/');
  const rootPath = isJapanese ? '/ja' : '';
  const source = [
    document.title,
    document.querySelector('.miyabiya-mobile-product-title')?.textContent,
    document.querySelector('.product-details .text-block:is(.h1, .h2)')?.textContent,
    nav.textContent,
    window.location.pathname,
  ].join(' ').toLowerCase();

  const series = (() => {
    if (source.includes('one piece') || source.includes('ワンピース')) return { en: 'ONE PIECE', ja: 'ONE PIECE', url: `${rootPath}/collections/one-piece` };
    if (source.includes('jujutsu kaisen') || source.includes('呪術廻戦')) return { en: 'Jujutsu Kaisen', ja: '呪術廻戦', url: `${rootPath}/collections/jujutsu-kaisen` };
    if (source.includes('chainsaw man') || source.includes('チェンソーマン')) return { en: 'Chainsaw Man', ja: 'チェンソーマン', url: `${rootPath}/collections/chainsaw-man` };
    if (source.includes('attack on titan') || source.includes('進撃の巨人')) return { en: 'Attack on Titan', ja: '進撃の巨人', url: `${rootPath}/search?type=product&q=attack%20on%20titan` };
    if (source.includes('oshi no ko') || source.includes('oshinoko') || source.includes('Oshi no Ko')) return { en: 'Oshi no Ko', ja: 'Oshi no Ko', url: `${rootPath}/search?type=product&q=oshi%20no%20ko` };
    if (source.includes('demon slayer') || source.includes('kimetsu') || source.includes('鬼滅の刃')) return { en: 'Demon Slayer', ja: '鬼滅の刃', url: `${rootPath}/collections/kimetsu-no-yaiba` };
    if (source.includes('dragon ball') || source.includes('ドラゴンボール')) return { en: 'Dragon Ball', ja: 'ドラゴンボール', url: `${rootPath}/collections/dragon-ball` };
    if (source.includes('naruto') || source.includes('ナルト')) return { en: 'Naruto', ja: 'ナルト', url: `${rootPath}/collections/naruto` };
    if (source.includes('pokemon') || source.includes('pokémon') || source.includes('ポケモン')) return { en: 'Pokémon', ja: 'ポケットモンスター', url: `${rootPath}/collections/pokemon` };
    return null;
  })();

  const character = (() => {
    if (source.includes('satoru gojo') || source.includes('五条悟')) return { en: 'Satoru Gojo', ja: '五条悟', url: `${rootPath}/collections/gojo-satoru-figure` };
    if (source.includes('levi') || source.includes('リヴァイ')) return { en: 'Levi', ja: 'リヴァイ', url: `${rootPath}/collections/levi-ackerman-figure` };
    if (source.includes('gear 5') || source.includes('gear-5') || source.includes('ギア5')) return { en: 'Gear 5 Luffy', ja: 'ギア5ルフィ', url: `${rootPath}/collections/gear-5-luffy-figure` };
    if (source.includes('luffy') || source.includes('ルフィ')) return { en: 'Luffy', ja: 'ルフィ', url: `${rootPath}/collections/luffy-figure` };
    if (source.includes('pochita') || source.includes('ポチタ')) return { en: 'Pochita', ja: 'ポチタ', url: `${rootPath}/collections/pochita-figure` };
    if (source.includes('aki hayakawa') || source.includes('早川アキ')) return { en: 'Aki Hayakawa', ja: '早川アキ', url: `${rootPath}/collections/aki-hayakawa-figure` };
    if (source.includes('makima') || source.includes('マキマ')) return { en: 'Makima', ja: 'マキマ', url: `${rootPath}/collections/makima-figure` };
    if (source.includes('power') || source.includes('パワー')) return { en: 'Power', ja: 'パワー', url: `${rootPath}/collections/power-figure` };
    if (source.includes('denji') || source.includes('デンジ')) return { en: 'Denji', ja: 'デンジ', url: `${rootPath}/collections/denji-figure` };
    if (/figure[-\s:]*resee|glamours\s*[-–—]\s*reze|figure\s+[\"“”']?reze[\"“”']?(?:\s|$)|[\"“”']reze[\"“”']/.test(source) || (source.includes('レゼ') && !source.includes('レゼ篇'))) return { en: 'Reze', ja: 'レゼ', url: `${rootPath}/collections/reze-figure` };
    if (source.includes('oshi no ko ai') || source.includes('星野アイ')) return { en: 'Ai Hoshino', ja: '星野アイ', url: `${rootPath}/search?type=product&q=ai%20hoshino` };
    if (source.includes('kana arima') || source.includes('有馬かな')) return { en: 'Kana Arima', ja: '有馬かな', url: `${rootPath}/search?type=product&q=kana%20arima` };
    return null;
  })();

  const homeLabel = isJapanese ? 'ホーム' : 'Home';
  const seriesLabel = series ? (isJapanese ? series.ja : series.en) : (isJapanese ? '商品' : 'Products');
  const seriesUrl = series?.url || `${rootPath}/collections/all`;
  const characterLabel = character ? (isJapanese ? character.ja : character.en) : '';
  const characterUrl = character?.url || '';
  const ol = document.createElement('ol');
  const addCrumb = (label, url, current = false) => {
    const li = document.createElement('li');
    const link = document.createElement('a');
    link.href = url || `${rootPath}/collections/all`;
    link.textContent = label;
    if (current) link.setAttribute('aria-current', 'page');
    li.appendChild(link);
    ol.appendChild(li);
  };

  addCrumb(homeLabel, rootPath || '/');
  addCrumb(seriesLabel, seriesUrl, !characterLabel);
  if (characterLabel) addCrumb(characterLabel, characterUrl, true);
  nav.setAttribute('aria-label', isJapanese ? 'パンくずリスト' : 'Breadcrumb');
  nav.replaceChildren(ol);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', normalizeMiyabiyaProductBreadcrumbs, { once: true });
} else {
  normalizeMiyabiyaProductBreadcrumbs();
}
window.addEventListener('load', normalizeMiyabiyaProductBreadcrumbs, { once: true });

function repairMiyabiyaReleaseCalendarHub() {
  const root = document.querySelector('.miyabiya-release-hub');
  const dataNode = document.getElementById('miyabiya-release-calendar-data');
  if (!root || !dataNode || root.dataset.miyabiyaCriticalHubRepair === 'true') return;
  if (root.dataset.releaseHubVersion) return;
  root.dataset.miyabiyaCriticalHubRepair = 'true';

  let releases = [];
  try {
    releases = JSON.parse(dataNode.textContent || '[]');
  } catch {
    return;
  }

  const escapeHtml = (value) => String(value || '').replace(/[&<>"']/g, (char) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
  })[char]);
  const compact = (value) => String(value || '').replace(/\s+/g, ' ').trim();
  const normalize = (value) => compact(value).toLowerCase();
  const releaseSearchAliases = [
    ['きめつ', '鬼滅 鬼滅の刃 kimetsu demon slayer'],
    ['キメツ', '鬼滅 鬼滅の刃 kimetsu demon slayer'],
    ['たんじろう', '炭治郎 竈門炭治郎 tanjiro kamado'],
    ['ねずこ', '禰豆子 竈門禰豆子 nezuko kamado'],
    ['ぜんいつ', '善逸 我妻善逸 zenitsu agatsuma'],
    ['いのすけ', '伊之助 嘴平伊之助 inosuke hashibira'],
    ['ぎゆう', '義勇 冨岡義勇 tomioka giyu'],
    ['しのぶ', '胡蝶しのぶ shinobu kocho'],
    ['じゅじゅつ', '呪術廻戦 jujutsu kaisen'],
    ['ごじょう', '五条悟 五条 gojo satoru'],
    ['ワンピース', 'ONE PIECE ワンピース onepiece'],
    ['わんぴーす', 'ONE PIECE ワンピース onepiece'],
    ['るふぃ', 'ルフィ モンキー・D・ルフィ luffy'],
    ['ぎんたま', '銀魂 gintama'],
    ['おしのこ', 'Oshi no Ko oshinoko'],
    ['しんげき', '進撃の巨人 attack on titan'],
    ['ちぇんそー', 'チェンソーマン chainsaw man'],
    ['ぶるーろっく', 'ブルーロック blue lock'],
    ['ぶるろ', 'ブルーロック blue lock'],
    ['ひろあか', '僕のヒーローアカデミア ヒロアカ my hero academia'],
    ['はいきゅー', 'ハイキュー haikyu haikyuu']
  ];
  const expandSearchText = (value) => {
    const base = normalize(value);
    if (!base) return '';
    const extras = [];
    releaseSearchAliases.forEach(([alias, words]) => {
      const aliasText = normalize(alias);
      const wordText = normalize(words);
      const tokens = wordText.split(/\s+/).filter(Boolean);
      if (base.includes(aliasText)) {
        extras.push(wordText);
      } else if (tokens.some((token) => base.includes(token))) {
        extras.push(aliasText, wordText);
      }
    });
    return (base + ' ' + extras.join(' ')).trim();
  };

  const categoryLabel = (value) => {
    const text = compact(value);
    if (!text) return '';
    if (text.includes('フィギュア') || text.includes('プラモデル') || text.includes('模型')) return 'フィギュア / プラモ';
    if (text.includes('アクリル')) return 'アクリルスタンド';
    if (text === 'バッチ類' || text.includes('バッジ') || text.includes('缶バ')) return '缶バッジ';
    if (text.includes('キーホルダー') || text.includes('ストラップ') || text.includes('チャーム')) return 'キーホルダー / アクキー';
    if (text === 'TCGグッズ' || text.includes('カード') || text.includes('トレカ') || text.includes('ブロマイド') || text.includes('フォト')) return 'カード / ブロマイド';
    if (text.includes('ぬいぐるみ') || text.includes('マスコット')) return 'ぬいぐるみ / マスコット';
    if (text.includes('アパレル') || text.includes('Tシャツ') || text.includes('パーカー') || text.includes('タオル') || text.includes('ハンカチ')) return 'アパレル / タオル';
    if (text.includes('文具') || text.includes('デスク') || text.includes('ステッカー') || text.includes('シール') || text.includes('ノート') || text.includes('クリアファイル')) return '文具 / ステッカー';
    if (text.includes('ポスター') || text.includes('タペストリー')) return 'ポスター / タペストリー';
    if (text.includes('バッグ') || text.includes('財布') || text.includes('ポーチ') || text.includes('ケース') || text.includes('収納')) return 'バッグ / ポーチ';
    if (text.includes('コミック') || text.includes('小説') || text.includes('雑誌') || text.includes('書籍') || text.includes('画集') || text.includes('写真集') || text.includes('設定集') || text.includes('資料集') || text.includes('ムック') || text.includes('絵本')) return '本 / コミック';
    if (text.includes('Blu-ray') || text.includes('DVD') || text.includes('CD') || text.includes('アルバム') || text.includes('主題歌') || text.includes('キャラクターソング') || text.includes('サウンドトラック') || text.includes('音楽') || text.includes('レコード')) return 'BD/DVD/CD';
    if (text.includes('Switch') || text.includes('PS5') || text.includes('Win') || text.includes('ゲーム')) return 'ゲーム / ソフト';
    return text === 'グッズ' ? 'その他グッズ' : text;
  };
  const categoryIconClass = (value) => {
    const label = categoryLabel(value);
    if (label.includes('フィギュア')) return 'figure';
    if (label.includes('アクリル')) return 'acrylic';
    if (label.includes('缶バッジ')) return 'badge';
    if (label.includes('キーホルダー')) return 'keyholder';
    if (label.includes('カード')) return 'card';
    if (label.includes('ぬいぐるみ')) return 'plush';
    if (label.includes('アパレル')) return 'apparel';
    if (label.includes('文具')) return 'stationery';
    if (label.includes('ポスター')) return 'poster';
    if (label.includes('本 / コミック')) return 'book';
    if (label.includes('BD/DVD/CD')) return 'media';
    return 'other';
  };
  const statusLabel = (value) => {
    const text = compact(value);
    if (!text || text === '予約受付中' || text === '発売情報のみ' || text === '情報掲載のみ') return '発売予定';
    return text;
  };
  const shortTitle = (value) => {
    const title = compact(value)
      .replace(/^【[^】]+】\s*/u, '')
      .replace(/【ポイント[^】]*】/gu, '')
      .replace(/【通販限定特典付き】/gu, '')
      .replace(/【アニメイト先行販売】/gu, '');
    return title.length > 42 ? `${title.slice(0, 42)}…` : title;
  };
  const shortDate = (value) => String(value || '').replace('2026年', '').replace(/\([月火水木金土日]\)/g, '').trim();
  const itemDay = (item) => Number(String(item.day || '').replace('5月', '').replace('日', '')) || Number(String(item.date || '').match(/05月?(\d{1,2})日?/)?.[1] || 0);
  const typeBadge = (item) => {
    const text = `${item.title || ''} ${item.character || ''}`;
    if (/全\d+種|全[一二三四五六七八九十]+種/.test(text)) return '全種あり';
    if (/BOX|ボックス/i.test(text)) return 'BOX商品';
    if (/トレーディング|ランダム/.test(text)) return 'ランダム商品';
    if (/再販/.test(text)) return '再販';
    return '';
  };

  const filters = Array.from(root.querySelectorAll('[data-release-filter]'));
  const results = root.querySelector('[data-release-results]');
  const resultsGrid = root.querySelector('[data-release-results-grid]');
  const resultsSummary = root.querySelector('[data-release-results-summary]');
  const resultsPagination = root.querySelector('[data-release-pagination]');
  const dayPanelTitle = root.querySelector('#release-day-panel-title');
  const dayPanelSummary = root.querySelector('[data-release-day-panel-summary]');
  const dayPanelList = root.querySelector('[data-release-day-panel-list]');
  const dayMoreButton = root.querySelector('[data-release-day-more]');
  let currentPage = Number.parseInt(new URLSearchParams(window.location.search).get('page') || '1', 10) || 1;
  let selectedDay = Number((new URLSearchParams(window.location.search).get('date') || '').split('-').pop()) || 27;
  let activeTab = root.querySelector('[data-release-tab].is-active')?.dataset.releaseTab || '今月発売';

  const currentState = () => {
    const state = {};
    filters.forEach((control) => { state[control.dataset.releaseFilter] = control.value || ''; });
    state.date = new URLSearchParams(window.location.search).get('date') || '';
    return state;
  };
  const clearDateFilter = () => {
    const params = new URLSearchParams(window.location.search);
    if (!params.has('date') && !params.has('page')) return;
    params.delete('date');
    params.delete('page');
    window.history?.replaceState?.(null, '', `/ja/pages/release-calendar${params.toString() ? `?${params}` : ''}#release-results`);
  };
  const matchesText = (haystack, needle) => !compact(needle) || expandSearchText(haystack).includes(expandSearchText(needle));
  const matchesTab = (item) => {
    const day = itemDay(item);
    if (activeTab === '今週発売') return day >= 15 && day <= 21;
    if (activeTab === '今月発売') return item.month === '2026年5月';
    if (activeTab === '来月発売') return item.month === '2026年6月';
    if (activeTab === '発売予定') return statusLabel(item.status) === '発売予定';
    if (activeTab === '再販予定') return statusLabel(item.status) === '再販予定';
    return true;
  };
  const filterItems = (state) => releases.filter((item) => {
    if (!matchesTab(item)) return false;
    if (state.date) {
      const day = Number(state.date.split('-').pop());
      if (day && itemDay(item) !== day) return false;
    }
    if (state.month && state.month !== 'すべて' && item.month !== state.month) return false;
    if (state.category && state.category !== 'すべて' && categoryLabel(item.category) !== categoryLabel(state.category)) return false;
    if (state.status && state.status !== 'すべて' && statusLabel(item.status) !== statusLabel(state.status)) return false;
    if (!matchesText(`${item.anime} ${item.title} ${item.character}`, state.anime)) return false;
    if (!matchesText(`${item.character} ${item.title}`, state.character)) return false;
    if (!matchesText(item.maker, state.maker)) return false;
    return true;
  });
  const hasActiveFilter = (state) => activeTab !== '今月発売' || Object.entries(state).some(([key, value]) => {
    if (!value) return false;
    if ((key === 'month' || key === 'category' || key === 'status') && value === 'すべて') return false;
    return true;
  });
  const cardMarkup = (item) => {
    const maker = item.maker ? `<span data-release-meta="maker"><b>メーカー</b> ${escapeHtml(item.maker)}</span>` : '';
    const price = item.price ? `<span data-release-meta="price"><b>参考価格</b> ${escapeHtml(item.price)}</span>` : '';
    const category = item.category ? `<span data-release-meta="category"><b>カテゴリ</b> ${escapeHtml(categoryLabel(item.category))}</span>` : '';
    const anime = item.anime ? `<span data-release-meta="anime"><b>作品</b> ${escapeHtml(item.anime)}</span>` : '';
    const character = item.character ? `<span data-release-meta="character"><b>キャラクター</b> ${escapeHtml(item.character)}</span>` : '';
    const date = item.date ? `<span data-release-meta="date"><b>発売予定</b> ${escapeHtml(item.date)}</span>` : '';
    const extraBadge = typeBadge(item);
    return `<article class="miyabiya-release-calendar-card">
      <div class="miyabiya-release-calendar-card__type" aria-hidden="true">
        <span class="miyabiya-release-category-art miyabiya-release-category-art--${escapeHtml(categoryIconClass(item.category))}"></span>
        <small>${escapeHtml(shortDate(item.date))}</small>
      </div>
      <div class="miyabiya-release-calendar-card__badges">
        <span class="miyabiya-release-calendar-card__badge">${escapeHtml(statusLabel(item.status))}</span>
        ${extraBadge ? `<span class="miyabiya-release-calendar-card__tag">${escapeHtml(extraBadge)}</span>` : ''}
      </div>
      <h4 title="${escapeHtml(item.title)}">${escapeHtml(shortTitle(item.title))}</h4>
      <div class="miyabiya-release-calendar-card__meta">${anime}${character}${date}${category}${maker}${price}</div>
    </article>`;
  };
  const buildUrl = (page = currentPage, overrides = {}) => {
    const state = { ...currentState(), ...overrides };
    const params = new URLSearchParams();
    if (state.date) params.set('date', state.date);
    if (state.month && state.month !== 'すべて') params.set('month', state.month);
    if (state.category && state.category !== 'すべて') params.set('category', categoryLabel(state.category));
    if (state.anime) params.set('anime', state.anime);
    if (state.character) params.set('character', state.character);
    if (state.maker) params.set('maker', state.maker);
    if (state.status && state.status !== 'すべて') params.set('status', statusLabel(state.status));
    if (page > 1) params.set('page', String(page));
    return `/ja/pages/release-calendar${params.toString() ? `?${params}` : ''}#release-results`;
  };
  const renderResults = (scroll = false) => {
    if (!results || !resultsGrid || !resultsSummary) return;
    const state = currentState();
    if (!hasActiveFilter(state)) {
      results.hidden = true;
      if (resultsPagination) resultsPagination.hidden = true;
      return;
    }
    const items = filterItems(state);
    const totalPages = Math.max(1, Math.ceil(items.length / 20));
    currentPage = Math.min(Math.max(1, currentPage), totalPages);
    const start = (currentPage - 1) * 20;
    const end = Math.min(start + 20, items.length);
    results.hidden = false;
    resultsSummary.textContent = `${items.length}件中 ${items.length ? start + 1 : 0}–${end}件を表示しています。`;
    resultsGrid.innerHTML = items.slice(start, end).map(cardMarkup).join('');
    if (resultsPagination) {
      resultsPagination.hidden = totalPages <= 1;
      resultsPagination.innerHTML = totalPages <= 1 ? '' : Array.from({ length: Math.min(totalPages, 5) }, (_, index) => {
        const page = index + 1;
        return page === currentPage ? `<span aria-current="page">${page}</span>` : `<a href="${escapeHtml(buildUrl(page))}">${page}</a>`;
      }).join('');
    }
    window.history?.replaceState?.(null, '', buildUrl(currentPage));
    if (scroll) results.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  const renderDayPanel = () => {
    if (!dayPanelTitle || !dayPanelSummary || !dayPanelList) return;
    const items = releases.filter((item) => itemDay(item) === selectedDay);
    const shown = items.slice(0, 10);
    dayPanelTitle.textContent = `5月${selectedDay}日の発売予定`;
    dayPanelSummary.textContent = `${items.length}件中${shown.length}件を表示しています。`;
    dayPanelList.innerHTML = shown.map(cardMarkup).join('');
    if (dayMoreButton) {
      dayMoreButton.hidden = items.length <= 10;
      dayMoreButton.textContent = `すべて見る（${items.length}件）`;
    }
    root.querySelectorAll('.miyabiya-release-hub__day.is-selected').forEach((node) => node.classList.remove('is-selected'));
    root.querySelector(`#cal-day-${selectedDay}`)?.classList.add('is-selected');
  };
  const repairStats = () => {
    const stats = root.querySelector('.miyabiya-release-hub__stats');
    if (!stats) return;
    const state = currentState();
    const monthItems = releases.filter((item) => !state.month || state.month === 'すべて' || item.month === state.month);
    const categoryCount = new Set(monthItems.map((item) => categoryLabel(item.category)).filter(Boolean)).size;
    const characterCount = new Set(
      monthItems
        .flatMap((item) => compact(item.character).split(/[、,／\/・]+/))
        .map((value) => value.trim())
        .filter(Boolean)
    ).size;
    stats.innerHTML = `
      <div><strong>${releases.length.toLocaleString('ja-JP')}</strong><span>現在掲載中の発売情報</span></div>
      <div><strong>${escapeHtml(state.month && state.month !== 'すべて' ? state.month : '2026年5月')}</strong><span>表示中の発売時期</span></div>
      <div><strong>${categoryCount.toLocaleString('ja-JP')}</strong><span>カテゴリ別に整理</span></div>
      <div><strong>${characterCount.toLocaleString('ja-JP')}キャラ</strong><span>掲載中のキャラクター</span></div>`;
  };

  filters.forEach((control) => {
    ['input', 'change'].forEach((eventName) => control.addEventListener(eventName, () => {
      currentPage = 1;
      clearDateFilter();
      repairStats();
      renderResults(false);
    }));
  });
  root.querySelector('[data-release-search]')?.addEventListener('click', () => {
    currentPage = 1;
    clearDateFilter();
    repairStats();
    renderResults(true);
  });
  root.querySelectorAll('[data-release-tab]').forEach((tab) => {
    tab.addEventListener('click', (event) => {
      event.preventDefault();
      activeTab = tab.dataset.releaseTab || '今月発売';
      root.querySelectorAll('[data-release-tab]').forEach((node) => node.classList.toggle('is-active', node === tab));
      currentPage = 1;
      clearDateFilter();
      renderResults(true);
    });
  });
  root.querySelectorAll('[data-release-day-link]').forEach((link) => {
    link.addEventListener('click', (event) => {
      if (event.target.closest('[data-release-day-more-link]')) return;
      event.preventDefault();
      selectedDay = Number(link.dataset.releaseDayLink || selectedDay);
      renderDayPanel();
    }, true);
  });
  root.querySelectorAll('[data-release-day-more-link]').forEach((button) => {
    button.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      const day = String(Number(button.dataset.releaseDayMoreLink || selectedDay)).padStart(2, '0');
      window.location.href = `/ja/pages/release-calendar?date=2026-05-${day}#release-results`;
    }, true);
  });
  dayMoreButton?.addEventListener('click', (event) => {
    event.preventDefault();
    const day = String(selectedDay).padStart(2, '0');
    const href = `/ja/pages/release-calendar?date=2026-05-${day}#release-results`;
    dayMoreButton.setAttribute('href', href);
    window.location.assign(href);
  }, true);
  root.querySelectorAll('[data-release-quick-filter]').forEach((link) => {
    link.addEventListener('click', (event) => {
      event.preventDefault();
      const target = root.querySelector(`[data-release-filter="${link.dataset.releaseQuickFilter}"]`);
      if (!target) return;
      target.value = link.dataset.releaseQuickValue || 'すべて';
      currentPage = 1;
      clearDateFilter();
      repairStats();
      renderResults(true);
    }, true);
  });
  root.querySelectorAll('[data-release-view]').forEach((button) => {
    button.addEventListener('click', (event) => {
      event.preventDefault();
      const view = button.dataset.releaseView || 'month';
      root.classList.toggle('is-list-view', view === 'list');
      root.querySelectorAll('[data-release-view]').forEach((node) => node.classList.toggle('is-active', node === button));
    }, true);
  });

  repairStats();
  renderDayPanel();
  renderResults(false);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', repairMiyabiyaReleaseCalendarHub);
} else {
  repairMiyabiyaReleaseCalendarHub();
}
window.addEventListener('load', repairMiyabiyaReleaseCalendarHub, { once: true });

function removeMiyabiyaReleaseCalendarHelperCopy() {
  const releaseRoot = document.querySelector('.miyabiya-release-hub');
  const releaseDataNode = document.getElementById('miyabiya-release-calendar-data');
  const normalizeReleaseCategoryLabel = (value) => {
    const text = String(value || '').trim();
    if (!text) return '';
    if (text.includes('フィギュア') || text.includes('プラモデル') || text.includes('模型')) return 'フィギュア / プラモ';
    if (text.includes('アクリル')) return 'アクリルスタンド';
    if (text === 'バッチ類' || text.includes('バッジ') || text.includes('缶バ')) return '缶バッジ';
    if (text.includes('キーホルダー') || text.includes('ストラップ') || text.includes('チャーム')) return 'キーホルダー / アクキー';
    if (text === 'TCGグッズ' || text.includes('カード') || text.includes('トレカ') || text.includes('ブロマイド') || text.includes('フォト')) return 'カード / ブロマイド';
    if (text.includes('ぬいぐるみ') || text.includes('マスコット')) return 'ぬいぐるみ / マスコット';
    if (text.includes('アパレル') || text.includes('Tシャツ') || text.includes('パーカー') || text.includes('タオル') || text.includes('ハンカチ')) return 'アパレル / タオル';
    if (text.includes('文具') || text.includes('デスク') || text.includes('ステッカー') || text.includes('シール') || text.includes('ノート') || text.includes('クリアファイル')) return '文具 / ステッカー';
    if (text.includes('ポスター') || text.includes('タペストリー')) return 'ポスター / タペストリー';
    if (text.includes('バッグ') || text.includes('財布') || text.includes('ポーチ') || text.includes('ケース') || text.includes('収納')) return 'バッグ / ポーチ';
    if (text.includes('コミック') || text.includes('小説') || text.includes('雑誌') || text.includes('書籍') || text.includes('画集') || text.includes('写真集') || text.includes('設定集') || text.includes('資料集') || text.includes('ムック') || text.includes('絵本')) return '本 / コミック';
    if (text.includes('Blu-ray') || text.includes('DVD') || text.includes('CD') || text.includes('アルバム') || text.includes('主題歌') || text.includes('キャラクターソング') || text.includes('サウンドトラック') || text.includes('音楽') || text.includes('レコード')) return 'BD/DVD/CD';
    if (text.includes('Switch') || text.includes('PS5') || text.includes('Win') || text.includes('ゲーム')) return 'ゲーム / ソフト';
    return text === 'グッズ' ? 'その他グッズ' : text;
  };

  document.querySelectorAll('.miyabiya-release-hub__section-title p').forEach((node) => {
    if (node.textContent?.includes('PCでは1日あたり代表商品だけを表示し')) {
      node.remove();
    }
    if (node.textContent?.includes('スマホでは上旬・中旬・下旬')) {
      node.textContent = '上旬・中旬・下旬のリストで確認できます。';
    }
  });
  const mobileListTitle = document.getElementById('release-mobile-list-title');
  if (mobileListTitle?.textContent?.includes('スマホ')) {
    mobileListTitle.textContent = '発売リスト';
  }
  if (releaseRoot && releaseDataNode) {
    try {
      const releases = JSON.parse(releaseDataNode.textContent || '[]');
      if (Array.isArray(releases) && releases.length > 0) {
        const counts = new Map();
        releases.forEach((item) => {
          const label = normalizeReleaseCategoryLabel(item.category) || 'その他グッズ';
          counts.set(label, (counts.get(label) || 0) + 1);
        });
        releaseRoot.querySelectorAll('[data-release-guide-count]').forEach((node) => {
          const label = node.dataset.releaseGuideCount || '';
          const normalizedLabel = normalizeReleaseCategoryLabel(label);
          node.textContent = `${counts.get(label) || counts.get(normalizedLabel) || 0}件`;
        });
      }
    } catch {
      // Keep the Liquid-rendered fallback if the JSON data is unavailable.
    }
  }
  document.querySelectorAll('.miyabiya-release-hub__filters').forEach((node) => {
    node.style.position = 'static';
    node.style.top = 'auto';
    node.style.zIndex = 'auto';
    node.style.backdropFilter = 'none';
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', removeMiyabiyaReleaseCalendarHelperCopy, { once: true });
} else {
  removeMiyabiyaReleaseCalendarHelperCopy();
}
window.addEventListener('load', removeMiyabiyaReleaseCalendarHelperCopy, { once: true });

function applyMiyabiyaCacheResistantFixes() {
  const path = window.location.pathname;

  if (path === '/' || path === '/ja' || path === '/ja/') {
    document.querySelectorAll('.miyakai-home-guide-banner').forEach((section) => {
      section.remove();
    });

    const featureImages = document.querySelectorAll('.miyabiya-home-feature-banners img');
    featureImages.forEach((image) => {
      image.loading = 'eager';
      image.decoding = 'async';
      image.setAttribute('fetchpriority', 'high');

      const picture = image.closest('picture');
      const source = picture?.querySelector('source[media*="max-width"]');
      const preloadHref = source?.srcset?.split(',')[0]?.trim()?.split(' ')[0] || image.currentSrc || image.src;
      if (
        !preloadHref ||
        Array.from(document.querySelectorAll('link[data-miyabiya-feature-preload]')).some(
          (node) => node.getAttribute('data-miyabiya-feature-preload') === preloadHref
        )
      ) {
        return;
      }

      const preload = document.createElement('link');
      preload.rel = 'preload';
      preload.as = 'image';
      preload.href = preloadHref;
      preload.media = source?.media || '';
      preload.setAttribute('data-miyabiya-feature-preload', preloadHref);
      document.head.append(preload);
    });

    if (path === '/') {
      const styleId = 'miyabiya-home-otakara-blog-position-fix';
      if (!document.getElementById(styleId)) {
        const style = document.createElement('style');
        style.id = styleId;
        style.textContent = `
          .miyabiya-home-blog-list__otakara-banner,
          .miyabiya-home-otakara-banner {
            display: block !important;
            width: min(100%, 1040px) !important;
            max-width: calc(100vw - 32px) !important;
            margin: 18px auto 0 !important;
            overflow: hidden !important;
            border-radius: 8px !important;
            background: #fff !important;
            line-height: 0 !important;
            box-shadow: 0 1px 0 rgba(17, 17, 17, 0.08) !important;
          }
          .miyabiya-home-blog-list__otakara-banner img,
          .miyabiya-home-otakara-banner img {
            display: block !important;
            width: 100% !important;
            height: auto !important;
          }
        `;
        document.head.append(style);
      }

      const blogInner = document.querySelector('.miyabiya-home-blog-list__inner');
      const blogGrid = document.querySelector('.miyabiya-home-blog-list__grid');
      const banners = Array.from(
        document.querySelectorAll('.miyabiya-home-blog-list__otakara-banner, .miyabiya-home-otakara-banner')
      ).filter((node) => node instanceof HTMLElement);
      const primaryBanner = banners[0];
      if (blogInner && blogGrid && primaryBanner) {
        primaryBanner.classList.add('miyabiya-home-blog-list__otakara-banner');
        if (primaryBanner instanceof HTMLAnchorElement) {
          primaryBanner.href = '/collections/otakara';
        }
        blogGrid.insertAdjacentElement('afterend', primaryBanner);
        banners.slice(1).forEach((banner) => banner.remove());
      }
    }
  }

  const isEnglishGuidePage =
    document.documentElement.lang?.toLowerCase().startsWith('en') &&
    (path === '/pages/how-to-order' ||
      path === '/pages/order-flow' ||
      path === '/pages/payment' ||
      path === '/pages/payment-methods');

  if (!isEnglishGuidePage || document.querySelector('.miyabiya-growth-page--locked')) return;

  const main = document.getElementById('MainContent');
  if (!main) return;

  const isPayment = path.includes('payment');
  main.classList.add('miyabiya-legal-policy-main');
  main.innerHTML = isPayment
    ? `<section class="miyabiya-growth-page miyabiya-growth-page--payment_methods miyabiya-growth-page--locked"><div class="miyabiya-growth-page__inner page-width"><div class="miyabiya-growth-page__hero"><p>Payment Methods</p><h1>Payment Methods</h1><span>You can pay securely through the payment methods available at checkout. Available options may vary depending on your country, device, and Shopify checkout settings.</span></div><div class="miyabiya-growth-page__grid miyabiya-growth-page__grid--four"><article><small>Payment</small><h2>1. Available payment options</h2><p>Available payment options may include credit card, Shop Pay, Apple Pay, Google Pay, PayPal, and other payment methods shown at checkout.</p></article><article><small>Payment</small><h2>2. When payment is charged</h2><p>Payment is processed at the time of checkout. The actual billing date may depend on your card issuer or payment provider.</p></article><article><small>Payment</small><h2>3. Currency</h2><p>Prices may be displayed in JPY, USD, or CAD depending on your selected region and currency. The final amount shown at checkout is the amount to be paid.</p></article><article><small>Payment</small><h2>4. Security</h2><p>Payments are processed through Shopify's secure checkout. MIYABIYA does not store full credit card details.</p></article></div><div class="miyabiya-growth-page__cta"><h2>Questions about payment or checkout?</h2><p>If you have questions about payment or checkout, please contact us before placing your order.</p><a href="/pages/contact">Contact MIYABIYA</a></div></div></section>`
    : `<section class="miyabiya-growth-page miyabiya-growth-page--order_flow miyabiya-growth-page--locked"><div class="miyabiya-growth-page__inner page-width"><div class="miyabiya-growth-page__hero"><p>How to Order</p><h1>How to Order</h1><span>Follow these steps to review your item, complete checkout, and receive shipping updates after your order is prepared.</span></div><div class="miyabiya-growth-page__timeline"><div><strong>1. Choose your item</strong><span>Browse MIYABIYA's anime figures, hobby goods, plush items, cards, and character goods.</span></div><div><strong>2. Check product details</strong><span>Review the product title, price, quantity, condition notes, and shipping information before ordering.</span></div><div><strong>3. Add to cart</strong><span>Add the item to your cart and proceed to checkout.</span></div><div><strong>4. Complete checkout</strong><span>Enter your shipping address and choose an available payment method.</span></div><div><strong>5. Order confirmation</strong><span>After checkout, you will receive an order confirmation email.</span></div><div><strong>6. Order preparation</strong><span>MIYABIYA will prepare your order after payment is confirmed.</span></div><div><strong>7. Shipping notification</strong><span>Once your order ships, you will receive a shipping notification with tracking information if available.</span></div><div><strong>8. Delivery</strong><span>Delivery time may vary depending on destination, carrier, customs, and local delivery conditions.</span></div></div></div></section>`;
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', applyMiyabiyaCacheResistantFixes, { once: true });
} else {
  applyMiyabiyaCacheResistantFixes();
}
window.addEventListener('load', applyMiyabiyaCacheResistantFixes, { once: true });
